/**
 * Insurance & Claims — Unified farmer insurance hub.
 *
 * Three tabs:
 *   1. Policies  – Active / Expired / Pending policies, Create Policy, summary cards
 *   2. Coverage  – Sum insured, DOC coverage, Perils, Deductible, Document download
 *   3. Claims    – Submit Claim, claim list table, status, payout, details modal
 *
 * Each tab embeds the existing PolicyManagement / ClaimsManagement logic where
 * appropriate and adds a new Coverage section.  All backend API contracts are
 * preserved — no backend changes required.
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Tabs,
  Tab,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  LinearProgress,
} from '@mui/material';
import {
  Policy as PolicyIcon,
  Assignment as ClaimsIcon,
  Shield as CoverageIcon,
  CheckCircle,
  Warning,
  Schedule,
  AttachMoney,
  Visibility,
  Download as DownloadIcon,
  Add,
  Info,
  Description,
  Close,
  BugReport,
  Water as WaterIcon,
} from '@mui/icons-material';
import { useAuth } from 'hooks/useAuth';
import { policiesAPI, claimsAPI, pondsAPI, farmsAPI, documentsAPI } from 'services/api';
import api from 'services/api';
import { useNavigate } from 'react-router-dom';
import PolicyManagement from './PolicyManagement';
import DOCProgressIndicator from 'shared/components/DOCProgressIndicator';

// ─── Tab Panel helper ────────────────────────────────────────
const TabPanel = ({ children, value, index }) => (
  <Box role="tabpanel" hidden={value !== index} sx={{ pt: 2 }}>
    {value === index && children}
  </Box>
);

// ═════════════════════════════════════════════════════════════
// COVERAGE TAB
// ═════════════════════════════════════════════════════════════
const CoverageTab = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [policies, setPolicies] = useState([]);
  const [ponds, setPonds] = useState([]);
  const [farms, setFarms] = useState([]);
  const [perils, setPerils] = useState([]);
  const [documents, setDocuments] = useState({});
  const [selectedPolicy, setSelectedPolicy] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);

  const loadData = useCallback(async () => {
    if (!user) return;
    try {
      setLoading(true);
      setError(null);

      const [polRes, farmRes, perilsRes] = await Promise.allSettled([
        policiesAPI.getAll(),
        farmsAPI.getAll(),
        api.get('/api/v1/perils'),
      ]);

      const allPolicies = polRes.status === 'fulfilled' ? polRes.value.data : [];
      const allFarms = farmRes.status === 'fulfilled' ? farmRes.value.data : [];
      const perilData =
        perilsRes.status === 'fulfilled' && perilsRes.value.data?.success
          ? perilsRes.value.data.data
          : [];

      const userFarms = allFarms.filter((f) => f.user_id === user.id);
      const userPonds = await pondsAPI.getAllForUser();
      const userPolicies = allPolicies.filter((p) =>
        userPonds.some((pond) => pond.id === p.pond_id),
      );

      setPolicies(userPolicies);
      setPonds(userPonds);
      setFarms(userFarms);
      setPerils(perilData);

      // Pre-load documents for each policy
      const docMap = {};
      await Promise.allSettled(
        userPolicies.map(async (p) => {
          try {
            const res = await documentsAPI.getByPolicy(p.id);
            docMap[p.id] = res.data || [];
          } catch {
            docMap[p.id] = [];
          }
        }),
      );
      setDocuments(docMap);
    } catch (err) {
      console.error('Coverage load error:', err);
      setError('Failed to load coverage data.');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // helpers
  const getPond = (pondId) => ponds.find((p) => p.id === pondId);
  const getFarm = (pondId) => {
    const pond = getPond(pondId);
    return farms.find((f) => f.id === pond?.farm_id);
  };

  const calcDOC = (policy) => {
    if (!policy.start_date && !policy.policy_start_date) return null;
    const start = new Date(policy.start_date || policy.policy_start_date);
    const end = new Date(policy.end_date || policy.policy_end_date);
    if (isNaN(start) || isNaN(end)) return null;
    return Math.ceil((end - start) / (1000 * 60 * 60 * 24));
  };

  const getPlanDetails = (plan) => {
    switch (plan) {
      case 'A':
        return { name: 'Basic', deductible: '10%', color: 'primary' };
      case 'B':
        return { name: 'Standard', deductible: '5%', color: 'secondary' };
      case 'C':
        return { name: 'Premium', deductible: '2%', color: 'success' };
      default:
        return { name: plan || 'N/A', deductible: 'N/A', color: 'default' };
    }
  };

  const handleViewDetail = (policy) => {
    setSelectedPolicy(policy);
    setDetailOpen(true);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  const activePolicies = policies.filter((p) => p.status === 'active');

  if (policies.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 6 }}>
        <CoverageIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
        <Typography variant="h6" color="textSecondary" gutterBottom>
          No coverage data available
        </Typography>
        <Typography variant="body2" color="textSecondary">
          Create a policy first to view your coverage information.
        </Typography>
      </Box>
    );
  }

  // Aggregate stats
  const totalSumInsured = policies
    .filter((p) => p.status === 'active')
    .reduce((s, p) => s + (parseFloat(p.coverage_amount) || 0), 0);
  const totalPremium = policies
    .filter((p) => p.status === 'active')
    .reduce((s, p) => s + (parseFloat(p.premium_amount) || 0), 0);

  return (
    <Box>
      {/* ── Summary cards ─────────────────────────── */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderTop: '3px solid #1976d2' }}>
            <CardContent>
              <Typography variant="overline" color="text.secondary">
                Total Sum Insured
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 700, color: 'primary.main' }}>
                ₹{totalSumInsured.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderTop: '3px solid #4caf50' }}>
            <CardContent>
              <Typography variant="overline" color="text.secondary">
                Active Policies
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 700, color: 'success.main' }}>
                {activePolicies.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderTop: '3px solid #ff9800' }}>
            <CardContent>
              <Typography variant="overline" color="text.secondary">
                Annual Premium
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 700, color: 'warning.main' }}>
                ₹{totalPremium.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderTop: '3px solid #9c27b0' }}>
            <CardContent>
              <Typography variant="overline" color="text.secondary">
                Covered Perils
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#9c27b0' }}>
                {perils.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* ── Per-policy coverage table ─────────────── */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Policy Coverage Details
          </Typography>
          <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell sx={{ fontWeight: 600 }}>Policy #</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Pond / Farm</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Plan</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    Sum Insured
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    DOC Coverage
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    Deductible
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600 }}>
                    Docs
                  </TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {policies.map((policy) => {
                  const pond = getPond(policy.pond_id);
                  const farm = getFarm(policy.pond_id);
                  const doc = calcDOC(policy);
                  const plan = getPlanDetails(policy.insurance_plan);
                  const policyDocs = documents[policy.id] || [];

                  return (
                    <TableRow key={policy.id} hover>
                      <TableCell>#{policy.id}</TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {pond?.pond_name || pond?.name || '—'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {farm?.name || ''}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip label={`Plan ${policy.insurance_plan} — ${plan.name}`} size="small" color={plan.color} />
                      </TableCell>
                      <TableCell align="right">
                        ₹{parseFloat(policy.coverage_amount || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                      </TableCell>
                      <TableCell align="right">{doc ? `${doc} days` : '—'}</TableCell>
                      <TableCell align="right">{plan.deductible}</TableCell>
                      <TableCell>
                        {policy.status === 'active' && (
                          <Chip label="Active" color="success" size="small" icon={<CheckCircle />} />
                        )}
                        {policy.status === 'pending' && (
                          <Chip label="Pending" color="warning" size="small" icon={<Schedule />} />
                        )}
                        {policy.status === 'expired' && <Chip label="Expired" size="small" />}
                        {!['active', 'pending', 'expired'].includes(policy.status) && (
                          <Chip label={policy.status} size="small" />
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {policyDocs.length > 0 ? (
                          <Tooltip title="Download policy documents">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => {
                                // Open first document URL or download
                                const doc = policyDocs[0];
                                if (doc.file_url || doc.url) {
                                  window.open(doc.file_url || doc.url, '_blank');
                                }
                              }}
                            >
                              <DownloadIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            —
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Tooltip title="View coverage detail">
                          <IconButton size="small" onClick={() => handleViewDetail(policy)}>
                            <Visibility fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* ── Covered Perils grid ───────────────────── */}
      {perils.length > 0 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Covered Perils
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Your insurance policies provide protection against the following perils.
            </Typography>
            <Grid container spacing={2}>
              {perils.map((peril, idx) => (
                <Grid item xs={12} sm={6} md={4} key={peril.id || idx}>
                  <Card
                    variant="outlined"
                    sx={{
                      borderLeft: `4px solid ${peril.severity === 'high' ? '#f44336' : peril.severity === 'medium' ? '#ff9800' : '#4caf50'}`,
                    }}
                  >
                    <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <BugReport sx={{ fontSize: 18, color: 'text.secondary' }} />
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                          {peril.name || peril.peril_name || 'Unknown'}
                        </Typography>
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        {peril.description || peril.peril_description || 'No description available'}
                      </Typography>
                      {peril.severity && (
                        <Box sx={{ mt: 0.5 }}>
                          <Chip
                            label={peril.severity}
                            size="small"
                            sx={{
                              fontSize: '0.65rem',
                              height: 20,
                              textTransform: 'capitalize',
                              bgcolor:
                                peril.severity === 'high'
                                  ? '#ffebee'
                                  : peril.severity === 'medium'
                                    ? '#fff3e0'
                                    : '#e8f5e9',
                              color:
                                peril.severity === 'high'
                                  ? '#c62828'
                                  : peril.severity === 'medium'
                                    ? '#e65100'
                                    : '#2e7d32',
                            }}
                          />
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* ── Coverage Detail Dialog ────────────────── */}
      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: 'primary.main', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CoverageIcon />
            <Typography variant="h6">Coverage Detail</Typography>
          </Box>
          <IconButton size="small" onClick={() => setDetailOpen(false)} sx={{ color: 'white' }}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 3, mt: 1 }}>
          {selectedPolicy && (() => {
            const pond = getPond(selectedPolicy.pond_id);
            const farm = getFarm(selectedPolicy.pond_id);
            const doc = calcDOC(selectedPolicy);
            const plan = getPlanDetails(selectedPolicy.insurance_plan);
            const policyDocs = documents[selectedPolicy.id] || [];

            return (
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Policy</Typography>
                  <Typography variant="body1" fontWeight={600}>#{selectedPolicy.id}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Plan</Typography>
                  <Typography variant="body1" fontWeight={600}>
                    Plan {selectedPolicy.insurance_plan} — {plan.name}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Pond</Typography>
                  <Typography variant="body1" fontWeight={600}>{pond?.pond_name || pond?.name || '—'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Farm</Typography>
                  <Typography variant="body1" fontWeight={600}>{farm?.name || '—'}</Typography>
                </Grid>

                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                </Grid>

                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Sum Insured</Typography>
                  <Typography variant="h6" color="primary.main" fontWeight={700}>
                    ₹{parseFloat(selectedPolicy.coverage_amount || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Premium</Typography>
                  <Typography variant="h6" color="warning.main" fontWeight={700}>
                    ₹{parseFloat(selectedPolicy.premium_amount || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">DOC Coverage</Typography>
                  <Typography variant="body1" fontWeight={600}>{doc ? `${doc} days` : '—'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Deductible</Typography>
                  <Typography variant="body1" fontWeight={600}>{plan.deductible}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Start Date</Typography>
                  <Typography variant="body1">
                    {selectedPolicy.start_date || selectedPolicy.policy_start_date
                      ? new Date(selectedPolicy.start_date || selectedPolicy.policy_start_date).toLocaleDateString()
                      : '—'}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">End Date</Typography>
                  <Typography variant="body1">
                    {selectedPolicy.end_date || selectedPolicy.policy_end_date
                      ? new Date(selectedPolicy.end_date || selectedPolicy.policy_end_date).toLocaleDateString()
                      : '—'}
                  </Typography>
                </Grid>

                {/* DOC Progress */}
                {(selectedPolicy.start_date || selectedPolicy.policy_start_date) && doc && (
                  <Grid item xs={12}>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>DOC Progress</Typography>
                    <DOCProgressIndicator
                      stockingDate={selectedPolicy.start_date || selectedPolicy.policy_start_date}
                      targetDOC={doc}
                      variant="linear"
                      size="medium"
                      showDetails
                      label="Days of Culture"
                    />
                  </Grid>
                )}

                {/* Documents */}
                {policyDocs.length > 0 && (
                  <Grid item xs={12}>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="subtitle2" gutterBottom>Policy Documents</Typography>
                    {policyDocs.map((d, i) => (
                      <Button
                        key={i}
                        size="small"
                        startIcon={<DownloadIcon />}
                        onClick={() => window.open(d.file_url || d.url, '_blank')}
                        sx={{ mr: 1, mb: 1 }}
                      >
                        {d.document_type || d.name || `Document ${i + 1}`}
                      </Button>
                    ))}
                  </Grid>
                )}
              </Grid>
            );
          })()}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setDetailOpen(false)} variant="outlined">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// ═════════════════════════════════════════════════════════════
// CLAIMS TAB — table-based view with detail modal
// ═════════════════════════════════════════════════════════════
const ClaimsTab = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [claims, setClaims] = useState([]);
  const [policies, setPolicies] = useState([]);
  const [ponds, setPonds] = useState([]);
  const [farms, setFarms] = useState([]);

  // Dialog states
  const [openDialog, setOpenDialog] = useState(false);
  const [editingClaim, setEditingClaim] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [viewingClaim, setViewingClaim] = useState(null);
  const [viewingClaimPolicy, setViewingClaimPolicy] = useState(null);

  const [formData, setFormData] = useState({
    policy_id: '',
    claim_type: 'disease',
    incident_date: '',
    description: '',
    estimated_loss: '',
    affected_area: '',
    cause_of_loss: '',
    supporting_documents: '',
  });

  const loadData = useCallback(async () => {
    if (!user) return;
    try {
      setLoading(true);
      setError(null);

      const [claimsRes, polRes, farmRes] = await Promise.all([
        claimsAPI.getAll(),
        policiesAPI.getAll(),
        farmsAPI.getAll(),
      ]);

      const userFarms = farmRes.data.filter((f) => f.user_id === user.id);
      const userPonds = await pondsAPI.getAllForUser();
      const userPolicies = polRes.data.filter((p) => userPonds.some((pond) => pond.id === p.pond_id));
      const userClaims = claimsRes.data.filter((c) => userPolicies.some((p) => p.id === c.policy_id));

      setClaims(userClaims);
      setPolicies(userPolicies);
      setPonds(userPonds);
      setFarms(userFarms);
    } catch (err) {
      console.error('Claims load error:', err);
      setError('Failed to load claims data.');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // ── helpers ──
  const getStatusColor = (status) => {
    if (!status) return 'default';
    switch (status) {
      case 'approved': return 'success';
      case 'pending': return 'warning';
      case 'rejected': return 'error';
      case 'under_review': return 'info';
      default: return 'default';
    }
  };

  const getClaimTypeLabel = (type) =>
    (type || 'unknown').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

  // ── open / close dialogs ──
  const handleOpenDialog = (claim = null) => {
    if (claim) {
      setEditingClaim(claim);
      setFormData({
        policy_id: claim.policy_id || '',
        claim_type: claim.claim_type || 'disease',
        incident_date: claim.incident_date ? claim.incident_date.split('T')[0] : '',
        description: claim.description || '',
        estimated_loss: claim.estimated_loss || '',
        affected_area: claim.affected_area || '',
        cause_of_loss: claim.cause_of_loss || '',
        supporting_documents: claim.supporting_documents || '',
      });
    } else {
      setEditingClaim(null);
      setFormData({
        policy_id: '',
        claim_type: 'disease',
        incident_date: '',
        description: '',
        estimated_loss: '',
        affected_area: '',
        cause_of_loss: '',
        supporting_documents: '',
      });
    }
    setFieldErrors({});
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingClaim(null);
  };

  // ── validation ──
  const validateForm = () => {
    const errors = {};
    if (!formData.policy_id) errors.policy_id = 'Please select a policy';
    if (!formData.incident_date) errors.incident_date = 'Incident date is required';
    if (!formData.description?.trim()) errors.description = 'Description is required';
    if (!formData.cause_of_loss?.trim()) errors.cause_of_loss = 'Cause of loss is required';
    if (!formData.estimated_loss || parseFloat(formData.estimated_loss) <= 0)
      errors.estimated_loss = 'Valid estimated loss is required';
    if (!formData.affected_area || parseFloat(formData.affected_area) <= 0)
      errors.affected_area = 'Valid affected area is required';
    if (formData.incident_date && new Date(formData.incident_date) > new Date())
      errors.incident_date = 'Incident date cannot be in the future';
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // ── submit ──
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setFieldErrors({});
    if (!validateForm()) return;

    try {
      const claimData = {
        ...formData,
        policy_id: parseInt(formData.policy_id),
        estimated_loss: parseFloat(formData.estimated_loss || 0),
        affected_area: parseFloat(formData.affected_area || 0),
        user_id: user.id,
      };

      if (editingClaim) {
        await claimsAPI.update(editingClaim.id, claimData);
      } else {
        await claimsAPI.create(claimData);
      }

      handleCloseDialog();
      loadData();
    } catch (err) {
      console.error('Claim save error:', err);
      if (err.response?.status === 422) {
        const detail = err.response.data?.detail;
        if (typeof detail === 'string') setError(detail);
        else if (Array.isArray(detail)) {
          const map = {};
          detail.forEach((e) => {
            if (e.loc?.length) map[e.loc[e.loc.length - 1]] = e.msg || 'Invalid';
          });
          setFieldErrors(map);
        } else setError('Validation error.');
      } else {
        setError('Failed to save claim.');
      }
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this claim?')) return;
    try {
      await claimsAPI.delete(id);
      loadData();
    } catch {
      setError('Failed to delete claim.');
    }
  };

  const handleViewDetail = async (claim) => {
    setViewingClaim(claim);
    setDetailOpen(true);
    if (claim.policy_id) {
      try {
        const res = await policiesAPI.getById(claim.policy_id);
        setViewingClaimPolicy(res.data);
      } catch {
        // silent
      }
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  // ── stat cards ──
  const pendingCount = claims.filter((c) => c.status === 'pending').length;
  const approvedCount = claims.filter((c) => c.status === 'approved').length;
  const rejectedCount = claims.filter((c) => c.status === 'rejected').length;
  const totalPayout = claims
    .filter((c) => c.status === 'approved')
    .reduce((s, c) => s + (parseFloat(c.approved_amount || c.payout_amount) || 0), 0);

  return (
    <Box>
      {/* ── Action bar ──────────────────────────── */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6">Your Claims</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={() => handleOpenDialog()}>
          Submit Claim
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* ── Stat cards ──────────────────────────── */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={3}>
          <Card>
            <CardContent sx={{ py: 1.5 }}>
              <Typography variant="overline" color="text.secondary">Total</Typography>
              <Typography variant="h5" fontWeight={700} color="primary.main">{claims.length}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card>
            <CardContent sx={{ py: 1.5 }}>
              <Typography variant="overline" color="text.secondary">Pending</Typography>
              <Typography variant="h5" fontWeight={700} color="warning.main">{pendingCount}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card>
            <CardContent sx={{ py: 1.5 }}>
              <Typography variant="overline" color="text.secondary">Approved</Typography>
              <Typography variant="h5" fontWeight={700} color="success.main">{approvedCount}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card>
            <CardContent sx={{ py: 1.5 }}>
              <Typography variant="overline" color="text.secondary">Total Payout</Typography>
              <Typography variant="h5" fontWeight={700} color="info.main">
                ₹{totalPayout.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* ── Claims table ────────────────────────── */}
      {claims.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <ClaimsIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="textSecondary" gutterBottom>
            No claims submitted yet
          </Typography>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Submit your first claim to get compensation for your losses.
          </Typography>
          <Button variant="contained" startIcon={<Add />} onClick={() => handleOpenDialog()} sx={{ mt: 2 }}>
            Submit Your First Claim
          </Button>
        </Box>
      ) : (
        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell sx={{ fontWeight: 600 }}>Claim #</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Policy</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Incident Date</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>Est. Loss</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>Payout</TableCell>
                <TableCell align="center" sx={{ fontWeight: 600 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {claims.map((claim) => {
                const policy = policies.find((p) => p.id === claim.policy_id);
                const pond = ponds.find((p) => p.id === policy?.pond_id);

                return (
                  <TableRow key={claim.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>
                        #{claim.claim_number || claim.id}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip label={getClaimTypeLabel(claim.claim_type)} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        #{policy?.id || '—'}{' '}
                        <Typography component="span" variant="caption" color="text.secondary">
                          {pond?.pond_name || pond?.name || ''}
                        </Typography>
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {claim.incident_date ? new Date(claim.incident_date).toLocaleDateString() : '—'}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={(claim.status || 'pending').replace(/_/g, ' ')}
                        color={getStatusColor(claim.status)}
                        size="small"
                        sx={{ textTransform: 'capitalize' }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      ₹{parseFloat(claim.estimated_loss || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                    </TableCell>
                    <TableCell align="right">
                      {claim.approved_amount || claim.payout_amount ? (
                        <Typography variant="body2" color="success.main" fontWeight={600}>
                          ₹{parseFloat(claim.approved_amount || claim.payout_amount || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                        </Typography>
                      ) : (
                        '—'
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="View details">
                        <IconButton size="small" onClick={() => handleViewDetail(claim)}>
                          <Visibility fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {claim.status === 'pending' && (
                        <Tooltip title="Delete">
                          <IconButton size="small" color="error" onClick={() => handleDelete(claim.id)}>
                            <Close fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* ── Submit / Edit Claim Dialog ────────────── */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle
          sx={{
            background: 'linear-gradient(135deg, #1976d2, #1565c0)',
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <ClaimsIcon />
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {editingClaim ? 'Edit Claim' : 'Submit New Claim'}
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.9 }}>
                {editingClaim ? 'Update claim information' : 'Report a new insurance claim'}
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={handleCloseDialog} sx={{ color: 'white' }}>
            <Close />
          </IconButton>
        </DialogTitle>

        <form onSubmit={handleSubmit}>
          <DialogContent sx={{ p: 3 }}>
            <Grid container spacing={2}>
              {/* Policy selection */}
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                  Policy *
                </Typography>
                <select
                  value={formData.policy_id}
                  onChange={(e) => setFormData({ ...formData, policy_id: e.target.value })}
                  required
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    borderRadius: 8,
                    border: fieldErrors.policy_id ? '1px solid #d32f2f' : '1px solid #ccc',
                    fontSize: 14,
                    background: 'white',
                  }}
                >
                  <option value="">Select Policy</option>
                  {policies
                    .filter((p) => p.status === 'active')
                    .map((p) => {
                      const pond = ponds.find((po) => po.id === p.pond_id);
                      return (
                        <option key={p.id} value={p.id}>
                          Policy #{p.id} — {pond?.pond_name || pond?.name || 'Unknown'}
                        </option>
                      );
                    })}
                </select>
                {fieldErrors.policy_id && (
                  <Typography variant="caption" color="error">
                    {fieldErrors.policy_id}
                  </Typography>
                )}
              </Grid>

              {/* Claim type */}
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                  Claim Type *
                </Typography>
                <select
                  value={formData.claim_type}
                  onChange={(e) => setFormData({ ...formData, claim_type: e.target.value })}
                  required
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    borderRadius: 8,
                    border: '1px solid #ccc',
                    fontSize: 14,
                    background: 'white',
                  }}
                >
                  <option value="disease">Disease Outbreak</option>
                  <option value="natural_disaster">Natural Disaster</option>
                  <option value="equipment_failure">Equipment Failure</option>
                  <option value="water_quality">Water Quality Issues</option>
                  <option value="predation">Predation</option>
                  <option value="other">Other</option>
                </select>
              </Grid>

              {/* Incident date */}
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                  Incident Date *
                </Typography>
                <input
                  type="date"
                  value={formData.incident_date}
                  onChange={(e) => setFormData({ ...formData, incident_date: e.target.value })}
                  required
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    borderRadius: 8,
                    border: fieldErrors.incident_date ? '1px solid #d32f2f' : '1px solid #ccc',
                    fontSize: 14,
                    background: 'white',
                  }}
                />
                {fieldErrors.incident_date && (
                  <Typography variant="caption" color="error">
                    {fieldErrors.incident_date}
                  </Typography>
                )}
              </Grid>

              {/* Cause */}
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                  Cause of Loss *
                </Typography>
                <input
                  type="text"
                  value={formData.cause_of_loss}
                  onChange={(e) => setFormData({ ...formData, cause_of_loss: e.target.value })}
                  required
                  placeholder="e.g., Bacterial infection, Storm damage"
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    borderRadius: 8,
                    border: fieldErrors.cause_of_loss ? '1px solid #d32f2f' : '1px solid #ccc',
                    fontSize: 14,
                    background: 'white',
                  }}
                />
                {fieldErrors.cause_of_loss && (
                  <Typography variant="caption" color="error">
                    {fieldErrors.cause_of_loss}
                  </Typography>
                )}
              </Grid>

              {/* Estimated loss */}
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                  Estimated Loss (₹) *
                </Typography>
                <input
                  type="number"
                  value={formData.estimated_loss}
                  onChange={(e) => setFormData({ ...formData, estimated_loss: e.target.value })}
                  required
                  placeholder="Amount"
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    borderRadius: 8,
                    border: fieldErrors.estimated_loss ? '1px solid #d32f2f' : '1px solid #ccc',
                    fontSize: 14,
                    background: 'white',
                  }}
                />
                {fieldErrors.estimated_loss && (
                  <Typography variant="caption" color="error">
                    {fieldErrors.estimated_loss}
                  </Typography>
                )}
              </Grid>

              {/* Affected area */}
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                  Affected Area (acres) *
                </Typography>
                <input
                  type="number"
                  step="0.1"
                  value={formData.affected_area}
                  onChange={(e) => setFormData({ ...formData, affected_area: e.target.value })}
                  required
                  placeholder="Area"
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    borderRadius: 8,
                    border: fieldErrors.affected_area ? '1px solid #d32f2f' : '1px solid #ccc',
                    fontSize: 14,
                    background: 'white',
                  }}
                />
                {fieldErrors.affected_area && (
                  <Typography variant="caption" color="error">
                    {fieldErrors.affected_area}
                  </Typography>
                )}
              </Grid>

              {/* Description */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                  Description *
                </Typography>
                <textarea
                  rows={3}
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  required
                  placeholder="Detailed description of the incident..."
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    borderRadius: 8,
                    border: fieldErrors.description ? '1px solid #d32f2f' : '1px solid #ccc',
                    fontSize: 14,
                    fontFamily: 'inherit',
                    resize: 'vertical',
                  }}
                />
                {fieldErrors.description && (
                  <Typography variant="caption" color="error">
                    {fieldErrors.description}
                  </Typography>
                )}
              </Grid>

              {/* Supporting docs */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                  Supporting Documents (optional)
                </Typography>
                <textarea
                  rows={2}
                  value={formData.supporting_documents}
                  onChange={(e) => setFormData({ ...formData, supporting_documents: e.target.value })}
                  placeholder="List any supporting documents..."
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    borderRadius: 8,
                    border: '1px solid #ccc',
                    fontSize: 14,
                    fontFamily: 'inherit',
                    resize: 'vertical',
                  }}
                />
              </Grid>
            </Grid>
          </DialogContent>

          <DialogActions sx={{ px: 3, py: 2, bgcolor: '#f8f9fa' }}>
            <Button onClick={handleCloseDialog} variant="outlined">
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              sx={{
                background: 'linear-gradient(135deg, #1976d2, #1565c0)',
                fontWeight: 600,
              }}
            >
              {editingClaim ? 'Update Claim' : 'Submit Claim'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* ── Claim Detail Modal ───────────────────── */}
      <Dialog open={detailOpen} onClose={() => { setDetailOpen(false); setViewingClaim(null); setViewingClaimPolicy(null); }} maxWidth="md" fullWidth>
        <DialogTitle sx={{ bgcolor: 'primary.main', color: 'white' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ClaimsIcon />
              <Typography variant="h6">Claim Details</Typography>
            </Box>
            <IconButton size="small" onClick={() => { setDetailOpen(false); setViewingClaim(null); setViewingClaimPolicy(null); }} sx={{ color: 'white' }}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          {viewingClaim && (() => {
            const policy = policies.find((p) => p.id === viewingClaim.policy_id);
            const pond = ponds.find((p) => p.id === policy?.pond_id);
            const farm = farms.find((f) => f.id === pond?.farm_id);

            return (
              <Grid container spacing={2} sx={{ mt: 0 }}>
                {/* Basic Info */}
                <Grid item xs={12}>
                  <Typography variant="subtitle1" fontWeight={600} color="primary">
                    Basic Information
                  </Typography>
                  <Divider sx={{ mb: 1 }} />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">Claim #</Typography>
                  <Typography fontWeight={600}>{viewingClaim.claim_number || viewingClaim.id}</Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">Status</Typography>
                  <Box>
                    <Chip label={(viewingClaim.status || 'pending').replace(/_/g, ' ')} color={getStatusColor(viewingClaim.status)} size="small" sx={{ textTransform: 'capitalize' }} />
                  </Box>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">Policy</Typography>
                  <Typography fontWeight={600}>#{viewingClaim.policy_id}</Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">Type</Typography>
                  <Typography>{getClaimTypeLabel(viewingClaim.claim_type)}</Typography>
                </Grid>

                {/* Incident */}
                <Grid item xs={12} sx={{ mt: 1 }}>
                  <Typography variant="subtitle1" fontWeight={600} color="primary">
                    Incident Details
                  </Typography>
                  <Divider sx={{ mb: 1 }} />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">Date</Typography>
                  <Typography>{viewingClaim.incident_date ? new Date(viewingClaim.incident_date).toLocaleDateString() : '—'}</Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">Cause</Typography>
                  <Typography>{viewingClaim.cause_of_loss || '—'}</Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">Estimated Loss</Typography>
                  <Typography fontWeight={600}>₹{parseFloat(viewingClaim.estimated_loss || 0).toLocaleString('en-IN')}</Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">Affected Area</Typography>
                  <Typography>{viewingClaim.affected_area || 0} acres</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">Description</Typography>
                  <Typography variant="body2">{viewingClaim.description || 'None'}</Typography>
                </Grid>

                {/* Financial */}
                {(viewingClaim.approved_amount || viewingClaim.payout_amount) && (
                  <>
                    <Grid item xs={12} sx={{ mt: 1 }}>
                      <Typography variant="subtitle1" fontWeight={600} color="primary">
                        Payout Information
                      </Typography>
                      <Divider sx={{ mb: 1 }} />
                    </Grid>
                    {viewingClaim.approved_amount && (
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">Approved Amount</Typography>
                        <Typography variant="h6" color="success.main" fontWeight={700}>
                          ₹{parseFloat(viewingClaim.approved_amount).toLocaleString('en-IN')}
                        </Typography>
                      </Grid>
                    )}
                    {viewingClaim.payout_amount && (
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">Payout Amount</Typography>
                        <Typography variant="h6" color="success.main" fontWeight={700}>
                          ₹{parseFloat(viewingClaim.payout_amount).toLocaleString('en-IN')}
                        </Typography>
                      </Grid>
                    )}
                  </>
                )}

                {/* Farm / Pond */}
                <Grid item xs={12} sx={{ mt: 1 }}>
                  <Typography variant="subtitle1" fontWeight={600} color="primary">
                    Farm &amp; Pond
                  </Typography>
                  <Divider sx={{ mb: 1 }} />
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Farm</Typography>
                  <Typography>{farm?.name || '—'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Pond</Typography>
                  <Typography>{pond?.pond_name || pond?.name || '—'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Submitted</Typography>
                  <Typography>{viewingClaim.created_at ? new Date(viewingClaim.created_at).toLocaleString() : '—'}</Typography>
                </Grid>

                {/* DOC progress */}
                {viewingClaimPolicy && (viewingClaimPolicy.policy_start_date || viewingClaimPolicy.start_date) && (
                  <Grid item xs={12} sx={{ mt: 1 }}>
                    <Divider sx={{ mb: 1 }} />
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>DOC at Time of Claim</Typography>
                    <DOCProgressIndicator
                      stockingDate={viewingClaimPolicy.policy_start_date || viewingClaimPolicy.start_date}
                      targetDOC={(() => {
                        try {
                          const s = new Date(viewingClaimPolicy.policy_start_date || viewingClaimPolicy.start_date);
                          const e = new Date(viewingClaimPolicy.policy_end_date || viewingClaimPolicy.end_date);
                          if (!isNaN(s) && !isNaN(e)) return Math.ceil((e - s) / (86400000));
                        } catch { /* */ }
                        return 120;
                      })()}
                      variant="linear"
                      size="medium"
                      showDetails
                      label="Days of Culture"
                    />
                  </Grid>
                )}
              </Grid>
            );
          })()}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button variant="outlined" onClick={() => { setDetailOpen(false); setViewingClaim(null); setViewingClaimPolicy(null); }}>
            Close
          </Button>
          {viewingClaim?.status === 'pending' && (
            <Button
              variant="contained"
              onClick={() => {
                setDetailOpen(false);
                handleOpenDialog(viewingClaim);
              }}
            >
              Edit Claim
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// ═════════════════════════════════════════════════════════════
// MAIN PAGE
// ═════════════════════════════════════════════════════════════
const InsuranceClaimsPage = () => {
  const [tab, setTab] = useState(0);

  return (
    <Box sx={{ p: { xs: 1, sm: 3 } }}>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
        Insurance &amp; Claims
      </Typography>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 1 }}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, minHeight: 48 },
          }}
        >
          <Tab icon={<PolicyIcon />} iconPosition="start" label="Policies" />
          <Tab icon={<CoverageIcon />} iconPosition="start" label="Coverage" />
          <Tab icon={<ClaimsIcon />} iconPosition="start" label="Claims" />
        </Tabs>
      </Box>

      <TabPanel value={tab} index={0}>
        <PolicyManagement />
      </TabPanel>
      <TabPanel value={tab} index={1}>
        <CoverageTab />
      </TabPanel>
      <TabPanel value={tab} index={2}>
        <ClaimsTab />
      </TabPanel>
    </Box>
  );
};

export default InsuranceClaimsPage;
